import React from 'react';

/**
 * FishboneDiagram - Visual SVG representation of an Ishikawa/Fishbone diagram
 * Displays cause-and-effect relationships in the classic fish-skeleton format
 * Uses category prefix + number labels on bones with a legend for full text
 */
const FishboneDiagram = ({ problemStatement, categories, rootCause }) => {
  // Configuration
  const width = 900;
  const height = 500;
  const spineY = height / 2;
  const spineStartX = 50;
  const spineEndX = width - 150;
  const headWidth = 130;

  // Category colors, prefixes for labels, matching the form
  const categoryConfig = {
    man: { name: 'Man', prefix: 'M', color: '#3B82F6', position: 'top', index: 0 },
    machine: { name: 'Machine', prefix: 'Mc', color: '#8B5CF6', position: 'top', index: 1 },
    method: { name: 'Method', prefix: 'Mt', color: '#10B981', position: 'top', index: 2 },
    material: { name: 'Material', prefix: 'Ma', color: '#F59E0B', position: 'bottom', index: 0 },
    measurement: { name: 'Measurement', prefix: 'Ms', color: '#EF4444', position: 'bottom', index: 1 },
    environment: { name: 'Environment', prefix: 'E', color: '#14B8A6', position: 'bottom', index: 2 }
  };

  // Calculate branch positions
  const branchSpacing = (spineEndX - spineStartX - headWidth) / 3.5;

  const getBranchX = (index) => spineStartX + 80 + (index * branchSpacing);

  // Truncate text to fit (used for effect box and root cause in SVG)
  const truncateText = (text, maxLength = 25) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Render a single bone with prefix+number label
  const renderCause = (index, branchX, isTop, color, prefix) => {
    const baseY = spineY + (isTop ? -40 : 40);
    const offsetY = isTop ? -(index * 25 + 15) : (index * 25 + 15);
    const causeY = baseY + offsetY;
    const causeEndX = branchX - 30 - (index * 15);
    const label = `${prefix}${index + 1}`;

    return (
      <g key={`cause-${index}`}>
        {/* Cause line */}
        <line
          x1={branchX - 10}
          y1={baseY + (isTop ? -index * 15 : index * 15)}
          x2={causeEndX}
          y2={causeY}
          stroke={color}
          strokeWidth="1.5"
          opacity="0.7"
        />
        {/* Cause label (prefix + number) */}
        <text
          x={causeEndX - 5}
          y={causeY + 1}
          textAnchor="end"
          fontSize="10"
          fontWeight="bold"
          fill={color}
          fontFamily="system-ui, sans-serif"
        >
          {label}
        </text>
      </g>
    );
  };

  // Render a category branch
  const renderBranch = (categoryKey) => {
    const config = categoryConfig[categoryKey];
    const causes = categories[categoryKey] || [];
    const validCauses = causes.filter(c => c && c.trim() !== '');
    const branchX = getBranchX(config.index);
    const isTop = config.position === 'top';
    const branchEndY = isTop ? spineY - 80 : spineY + 80;

    return (
      <g key={categoryKey}>
        {/* Main branch line */}
        <line
          x1={branchX}
          y1={spineY}
          x2={branchX - 60}
          y2={branchEndY}
          stroke={config.color}
          strokeWidth="3"
        />

        {/* Category label background */}
        <rect
          x={branchX - 110}
          y={branchEndY + (isTop ? -25 : 5)}
          width="100"
          height="20"
          rx="4"
          fill={config.color}
          opacity="0.9"
        />

        {/* Category label */}
        <text
          x={branchX - 60}
          y={branchEndY + (isTop ? -10 : 20)}
          textAnchor="middle"
          fontSize="11"
          fontWeight="bold"
          fill="white"
          fontFamily="system-ui, sans-serif"
        >
          {config.name}
        </text>

        {/* Render causes with prefix labels */}
        {validCauses.slice(0, 4).map((_, idx) =>
          renderCause(idx, branchX - 30, isTop, config.color, config.prefix)
        )}
      </g>
    );
  };

  // Check if any category has causes for the legend
  const hasAnyCauses = Object.keys(categoryConfig).some(key => {
    const causes = categories[key] || [];
    return causes.some(c => c && c.trim() !== '');
  });

  return (
    <div>
      <div className="bg-white rounded-lg p-4 overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full max-w-4xl mx-auto"
          style={{ minWidth: '700px' }}
        >
          {/* Background */}
          <rect width={width} height={height} fill="#F9FAFB" rx="8" />

          {/* Main spine (backbone) */}
          <line
            x1={spineStartX}
            y1={spineY}
            x2={spineEndX}
            y2={spineY}
            stroke="#1B5C94"
            strokeWidth="4"
          />

          {/* Arrow at the end (pointing to effect) */}
          <polygon
            points={`${spineEndX},${spineY} ${spineEndX - 15},${spineY - 10} ${spineEndX - 15},${spineY + 10}`}
            fill="#1B5C94"
          />

          {/* Fish head (effect/problem box) */}
          <rect
            x={spineEndX - 5}
            y={spineY - 50}
            width={headWidth + 20}
            height="100"
            rx="8"
            fill="#DC2626"
            opacity="0.9"
          />
          <text
            x={spineEndX + headWidth / 2 + 5}
            y={spineY - 32}
            textAnchor="middle"
            fontSize="11"
            fontWeight="bold"
            fill="white"
            fontFamily="system-ui, sans-serif"
          >
            EFFECT
          </text>
          <foreignObject
            x={spineEndX}
            y={spineY - 20}
            width={headWidth + 10}
            height="65"
          >
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              className="text-xs text-white text-center px-1 leading-tight"
              style={{
                fontSize: '10px',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                overflow: 'hidden',
                maxHeight: '65px',
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {problemStatement || 'Problem Statement'}
            </div>
          </foreignObject>

          {/* Render all category branches */}
          {Object.keys(categoryConfig).map(key => renderBranch(key))}

          {/* Root cause box (if identified) */}
          {rootCause && rootCause.trim() !== '' && (
            <g>
              <rect
                x={spineStartX}
                y={height - 70}
                width={width - 100}
                height="60"
                rx="6"
                fill="#059669"
                opacity="0.9"
              />
              <foreignObject
                x={spineStartX + 10}
                y={height - 65}
                width={width - 120}
                height="50"
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{
                    fontSize: '11px',
                    color: 'white',
                    fontFamily: 'system-ui, sans-serif',
                    lineHeight: '1.4',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    overflow: 'hidden',
                    maxHeight: '50px',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  <strong>ROOT CAUSE:</strong> {rootCause}
                </div>
              </foreignObject>
            </g>
          )}

          {/* Title */}
          <text
            x={width / 2}
            y={25}
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
            fill="#1B5C94"
            fontFamily="system-ui, sans-serif"
          >
            FISHBONE (ISHIKAWA) DIAGRAM
          </text>
        </svg>
      </div>

      {/* Legend - maps prefix+number labels to full cause text */}
      {hasAnyCauses && (
        <div className="mt-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="text-sm font-bold text-gray-700 mb-3">Legend</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {Object.entries(categoryConfig).map(([key, config]) => {
              const causes = (categories[key] || []).filter(c => c && c.trim() !== '');
              const validCauses = causes.slice(0, 4);
              if (validCauses.length === 0) return null;
              return (
                <div key={key}>
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: config.color }}
                    ></span>
                    <span className="text-xs font-bold text-gray-700">{config.name}</span>
                  </div>
                  {validCauses.map((cause, idx) => (
                    <div key={idx} className="text-xs text-gray-600 ml-5 leading-relaxed">
                      <span className="font-semibold" style={{ color: config.color }}>
                        {config.prefix}{idx + 1}
                      </span>
                      <span> &mdash; {cause}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FishboneDiagram;
